// Built-in pipe fittings (owner via Betty, 2026-10-06): 90° and 45° bends, equal and
// reducing tees (the branch reduces), and reducers, for 40, 50, 80, 100, 150, 200 and 250 mm.
// Bends also come in 125 mm (3.9.1), the size of the Davey pump inlets.
//
// Each fitting is ONE group of plain lines and arcs, made like a saved library object
// (centred on 0,0, millimetres), so placing, moving, snapping and exporting need nothing new.
// The drawn pipe width is the nominal size: a 100 mm fitting is drawn 100 mm across. The
// SIZES table below is the only place to change that if real outside diameters are wanted.
//
// Every fitting carries its turning point on its inlet end (the end you connect first), so
// Q / the Rotation box turn it about that end. The group's meta.fitting says what it is
// (kind, sizes) for a later parts list.
import type { Vec } from "../core/geometry";
import type { Entity, Style } from "../doc/entities";
import type { LibraryItem } from "./library";
import type { FittingPortData } from "../pipes/api";

/** Nominal sizes of tees and reducers, millimetres. */
export const PIPE_SIZES = [40, 50, 80, 100, 150, 200, 250] as const;
/** Nominal sizes of bends, millimetres: the sizes above and 125. */
export const BEND_SIZES = [40, 50, 80, 100, 125, 150, 200, 250] as const;

/**
 * teeup / teedown (3.15.1): a tee whose branch rises or drops, seen from above.
 * bendup / benddown (3.22.0): a 90° bend whose outlet rises or drops, seen from above. cap (3.22.0): an end cap.
 */
export type FittingKind = "bend90" | "bend45" | "tee" | "reducer" | "teeup" | "teedown" | "bendup" | "benddown" | "cap";
export interface FittingInfo {
  kind: FittingKind;
  /** Run / large end, mm. */
  size: number;
  /** Branch (tee) or small end (reducer), mm. Same as size for an equal tee. */
  size2?: number;
}

export const FITTING_CATEGORIES = {
  bend90: "Pipe fittings · 90° bends",
  bend45: "Pipe fittings · 45° bends",
  tee: "Pipe fittings · Tees",
  reducer: "Pipe fittings · Reducers",
  teeup: "Pipe fittings · Tees, branch up",
  teedown: "Pipe fittings · Tees, branch down",
  bendup: "Pipe fittings · 90° bends, up",
  benddown: "Pipe fittings · 90° bends, down",
  cap: "Pipe fittings · End caps"
} as const;

export const OUTLINE: Style = {
  strokeColor: "#e8eaf0",
  strokeWidth: 1.5,
  widthMode: "screen",
  dash: "solid",
  fill: { mode: "none", color: "#4dd0e1", opacity: 0.35 },
  opacity: 1
};
export const CENTRE: Style = { ...OUTLINE, strokeWidth: 0.75, dash: "dashdot", opacity: 0.8 };
/** Hidden lines (below the plan): dashed. */
export const HIDDEN: Style = { ...OUTLINE, dash: "dashed" };

/** Socket wall and socket depth for a pipe of nominal size d. */
const wall = (d: number) => Math.max(3, Math.round(d * 0.08));
const depth = (d: number) => Math.round(d * 0.6);

/**
 * 3.24.0: the vertical end of a bend up or down, from its inlet end (for the load repair of bends placed before 3.24.0).
 * The leg is the bend's radius (the nominal size) plus the inlet socket depth long; the end is at the centre of the
 * circle, and a pipe from it goes on in line with the leg until a person turns it.
 */
export function bendVerticalEnd(inlet: FittingPortData): FittingPortData {
  const L = inlet.size + depth(inlet.size);
  return { p: { x: inlet.p.x - inlet.dir.x * L, y: inlet.p.y - inlet.dir.y * L }, dir: { x: -inlet.dir.x, y: -inlet.dir.y }, size: inlet.size, vertical: true };
}

class Builder {
  readonly entities: Entity[] = [];
  private n = 0;
  constructor(private readonly key: string) {}
  private id(): string {
    return `${this.key}-${++this.n}`;
  }
  line(points: Vec[], closed = false, style: Style = OUTLINE): void {
    this.entities.push({ id: this.id(), type: "polyline", layerId: "fittings", locked: false, hidden: false, points, closed, style: { ...style } });
  }
  arc(center: Vec, r: number, startDeg: number, sweepDeg: number, style: Style = OUTLINE): void {
    this.entities.push({ id: this.id(), type: "arc", layerId: "fittings", locked: false, hidden: false, center, r, startDeg, sweepDeg, style: { ...style } });
  }
  /** A socket band: a box `len` long and `width` across, starting at `at` and running along unit `dir`. */
  socket(at: Vec, dir: Vec, len: number, width: number): void {
    const n = { x: -dir.y, y: dir.x };
    const h = width / 2;
    const end = { x: at.x + dir.x * len, y: at.y + dir.y * len };
    this.line(
      [
        { x: at.x + n.x * h, y: at.y + n.y * h },
        { x: end.x + n.x * h, y: end.y + n.y * h },
        { x: end.x - n.x * h, y: end.y - n.y * h },
        { x: at.x - n.x * h, y: at.y - n.y * h }
      ],
      true
    );
  }
}

/**
 * What a fitting builder makes. ports = the open ends, where a pipe centre line starts (the
 * dash-dot centre lines end exactly there); dir points OUT of the fitting. Order (src/pipes/api.ts):
 * bend [inlet, outlet]; tee [run inlet (left), run outlet (right), branch]; reducer [large, small].
 */
interface Made {
  b: Builder;
  inlet: Vec;
  ports: FittingPortData[];
}

const rad = (deg: number) => (deg * Math.PI) / 180;
const onCircle = (c: Vec, r: number, deg: number): Vec => ({ x: c.x + r * Math.cos(rad(deg)), y: c.y + r * Math.sin(rad(deg)) });

/**
 * A bend of `angle` degrees. The flow enters at the top going right and turns clockwise on
 * screen. Centre-line radius = the nominal size (a short-radius fitting).
 */
function bend(d: number, angle: 90 | 45): Made {
  const b = new Builder(`bend${angle}-${d}`);
  const R = d;
  const c = { x: 0, y: 0 };
  const a0 = 270;
  const a1 = 270 + angle;
  b.arc(c, R - d / 2, a0, angle);
  b.arc(c, R + d / 2, a0, angle);
  b.line([onCircle(c, R - d / 2, a0), onCircle(c, R + d / 2, a0)]);
  b.line([onCircle(c, R - d / 2, a1), onCircle(c, R + d / 2, a1)]);
  b.arc(c, R, a0, angle, CENTRE);
  const s = depth(d);
  const w = d + 2 * wall(d);
  const pA = onCircle(c, R, a0);
  const tA = { x: -1, y: 0 }; // backwards from the inlet face
  b.socket(pA, tA, s, w);
  const pB = onCircle(c, R, a1);
  const tB = { x: -Math.sin(rad(a1)), y: Math.cos(rad(a1)) }; // onwards from the outlet face
  b.socket(pB, tB, s, w);
  b.line([{ x: pA.x - s, y: pA.y }, pA], false, CENTRE);
  b.line([pB, { x: pB.x + tB.x * s, y: pB.y + tB.y * s }], false, CENTRE);
  const outlet = { x: pB.x + tB.x * s, y: pB.y + tB.y * s };
  return {
    b,
    inlet: { x: pA.x - s, y: pA.y },
    ports: [
      { p: { x: pA.x - s, y: pA.y }, dir: { x: -1, y: 0 }, size: d },
      { p: outlet, dir: tB, size: d }
    ]
  };
}

/** A tee: the run left to right (size D), the branch up (size d, d <= D). */
function tee(D: number, d: number): Made {
  const b = new Builder(`tee-${D}-${d}`);
  const Lr = Math.max(0.8 * D, d / 2 + 0.3 * D);
  const Lb = D / 2 + Math.max(0.5 * d, 0.35 * D);
  b.line(
    [
      { x: -Lr, y: -D / 2 },
      { x: -d / 2, y: -D / 2 },
      { x: -d / 2, y: -Lb },
      { x: d / 2, y: -Lb },
      { x: d / 2, y: -D / 2 },
      { x: Lr, y: -D / 2 },
      { x: Lr, y: D / 2 },
      { x: -Lr, y: D / 2 }
    ],
    true
  );
  const s = depth(D);
  const w = D + 2 * wall(D);
  b.socket({ x: -Lr, y: 0 }, { x: -1, y: 0 }, s, w);
  b.socket({ x: Lr, y: 0 }, { x: 1, y: 0 }, s, w);
  const sb = depth(d);
  b.socket({ x: 0, y: -Lb }, { x: 0, y: -1 }, sb, d + 2 * wall(d));
  b.line([{ x: -Lr - s, y: 0 }, { x: Lr + s, y: 0 }], false, CENTRE);
  b.line([{ x: 0, y: 0 }, { x: 0, y: -Lb - sb }], false, CENTRE);
  return {
    b,
    inlet: { x: -Lr - s, y: 0 },
    ports: [
      { p: { x: -Lr - s, y: 0 }, dir: { x: -1, y: 0 }, size: D },
      { p: { x: Lr + s, y: 0 }, dir: { x: 1, y: 0 }, size: D },
      { p: { x: 0, y: -Lb - sb }, dir: { x: 0, y: -1 }, size: d }
    ]
  };
}

/**
 * A tee whose branch goes UP (a riser) or DOWN (a drop), seen from above (owner, 2026-10-20: "tee branching up and tee
 * branching down for rising and dropping pipes, all sizes"). The run is drawn as the tee's run, left to right (size D).
 * The branch (size d) goes out of the drawing. It is drawn as a floor penetration (owner, 2026-10-20): the pipe circle
 * (size d) and the tee's collar round it (d + 2 x the socket wall, "thicker by a few mm"):
 *   UP   - solid circles;
 *   DOWN - dashed circles: hidden, below.
 * Ends: [run inlet (left), run outlet (right), branch]. 3.24.0 (owner, 2026-10-23: "a T facing up can not create a pipe
 * from the component"): the branch is a VERTICAL end at the centre of the circle, so a pipe can start there. Every size
 * has the same parts.
 */
function teeVertical(D: number, d: number, up: boolean): Made {
  const b = new Builder(`tee${up ? "up" : "down"}-${D}-${d}`);
  const Lr = Math.max(0.8 * D, d / 2 + 0.3 * D);
  b.line(
    [
      { x: -Lr, y: -D / 2 },
      { x: Lr, y: -D / 2 },
      { x: Lr, y: D / 2 },
      { x: -Lr, y: D / 2 }
    ],
    true
  );
  const s = depth(D);
  const w = D + 2 * wall(D);
  b.socket({ x: -Lr, y: 0 }, { x: -1, y: 0 }, s, w);
  b.socket({ x: Lr, y: 0 }, { x: 1, y: 0 }, s, w);
  b.line([{ x: -Lr - s, y: 0 }, { x: Lr + s, y: 0 }], false, CENTRE);
  // the branch: the pipe circle and the collar circle, each as two half circles (a whole-circle arc has the same start
  // and end, and draws nothing in a picture)
  const c = { x: 0, y: 0 };
  const look = up ? OUTLINE : HIDDEN;
  for (const r of [d / 2, d / 2 + wall(d)]) {
    b.arc(c, r, 0, 180, look);
    b.arc(c, r, 180, 180, look);
  }
  return {
    b,
    inlet: { x: -Lr - s, y: 0 },
    ports: [
      { p: { x: -Lr - s, y: 0 }, dir: { x: -1, y: 0 }, size: D },
      { p: { x: Lr + s, y: 0 }, dir: { x: 1, y: 0 }, size: D },
      { p: { x: 0, y: 0 }, dir: { x: 0, y: -1 }, size: d, vertical: true }
    ]
  };
}

/**
 * A 90° bend whose outlet goes UP (a riser) or DOWN (a drop), seen from above (owner, 2026-10-21: "90 degree bends ...
 * drawn as a pipe that ends in a circle that then works like a floor penetration so I can represent a vertical 90
 * degree joint"). The inlet comes in from the left (size d), through its socket, to the centre of the bend; there the
 * outlet leaves the drawing. The outlet is drawn as the floor penetration of the tees up and down: the pipe circle and
 * the collar round it, solid when it rises, dashed (hidden, below) when it drops. Ends: [inlet, outlet]. 3.24.0 (owner,
 * 2026-10-23): the outlet is a VERTICAL end at the centre of the circle, so a pipe can start there. The leg is the
 * bend's radius long (the nominal size, as the plain bends).
 */
function bendVertical(d: number, up: boolean): Made {
  const b = new Builder(`bend${up ? "up" : "down"}-${d}`);
  const R = d;
  const rc = d / 2 + wall(d); // the collar
  const meet = -Math.sqrt(rc * rc - (d / 2) * (d / 2)); // where the sides of the leg meet the collar
  b.line([{ x: -R, y: -d / 2 }, { x: meet, y: -d / 2 }]);
  b.line([{ x: -R, y: d / 2 }, { x: meet, y: d / 2 }]);
  b.line([{ x: -R, y: -d / 2 }, { x: -R, y: d / 2 }]); // the inlet face of the bend
  const s = depth(d);
  b.socket({ x: -R, y: 0 }, { x: -1, y: 0 }, s, d + 2 * wall(d));
  b.line([{ x: -R - s, y: 0 }, { x: 0, y: 0 }], false, CENTRE);
  const look = up ? OUTLINE : HIDDEN;
  for (const r of [d / 2, rc]) {
    b.arc({ x: 0, y: 0 }, r, 0, 180, look);
    b.arc({ x: 0, y: 0 }, r, 180, 180, look);
  }
  const inletEnd: FittingPortData = { p: { x: -R - s, y: 0 }, dir: { x: -1, y: 0 }, size: d };
  return { b, inlet: { x: -R - s, y: 0 }, ports: [inletEnd, bendVerticalEnd(inletEnd)] };
}

/**
 * An end cap (owner, 2026-10-21: "add End Caps to the pipe tool and in the library pallet"): a socket that the pipe
 * goes into, closed at the far end. The end of the pipe is at the open end (left); the cap is a socket depth long,
 * then the closed end, one socket wall thick. A line across shows where the pipe stops inside. One end.
 */
function cap(d: number): Made {
  const b = new Builder(`cap-${d}`);
  const s = depth(d);
  const t = wall(d);
  const w = d + 2 * t;
  b.socket({ x: 0, y: 0 }, { x: 1, y: 0 }, s + t, w);
  b.line([{ x: s, y: -w / 2 }, { x: s, y: w / 2 }], false, HIDDEN); // the pipe stops here, inside the cap
  b.line([{ x: 0, y: 0 }, { x: s + t, y: 0 }], false, CENTRE);
  return { b, inlet: { x: 0, y: 0 }, ports: [{ p: { x: 0, y: 0 }, dir: { x: -1, y: 0 }, size: d }] };
}

/** "Tee · 100 mm · branch up" / "Tee · 100 mm, branch 50 mm up" */
const teeVerticalName = (D: number, d: number, up: boolean): string =>
  d === D ? `Tee · ${D} mm · branch ${up ? "up" : "down"}` : `Tee · ${D} mm, branch ${d} mm ${up ? "up" : "down"}`;

/** A concentric reducer: the large end (D) on the left, the small end (d) on the right. */
function reducer(D: number, d: number): Made {
  const b = new Builder(`reducer-${D}-${d}`);
  const a = depth(D);
  const e = depth(d);
  const taper = Math.max(1.2 * (D - d), 0.3 * D);
  const L = a + taper + e;
  const x0 = -L / 2;
  b.line(
    [
      { x: x0, y: -D / 2 },
      { x: x0 + a, y: -D / 2 },
      { x: x0 + a + taper, y: -d / 2 },
      { x: x0 + L, y: -d / 2 },
      { x: x0 + L, y: d / 2 },
      { x: x0 + a + taper, y: d / 2 },
      { x: x0 + a, y: D / 2 },
      { x: x0, y: D / 2 }
    ],
    true
  );
  b.line([{ x: x0 + a, y: -D / 2 }, { x: x0 + a, y: D / 2 }]); // end of the large socket
  b.line([{ x: x0 + a + taper, y: -d / 2 }, { x: x0 + a + taper, y: d / 2 }]); // start of the small socket
  b.line([{ x: x0, y: 0 }, { x: x0 + L, y: 0 }], false, CENTRE);
  return {
    b,
    inlet: { x: x0, y: 0 },
    ports: [
      { p: { x: x0, y: 0 }, dir: { x: -1, y: 0 }, size: D },
      { p: { x: x0 + L, y: 0 }, dir: { x: 1, y: 0 }, size: d }
    ]
  };
}

/** Bounds of the drawn geometry (arcs by their sweep, sampled). */
function bounds(entities: Entity[]): { minX: number; minY: number; maxX: number; maxY: number } {
  let minX = Infinity,
    minY = Infinity,
    maxX = -Infinity,
    maxY = -Infinity;
  const add = (p: Vec) => {
    minX = Math.min(minX, p.x);
    minY = Math.min(minY, p.y);
    maxX = Math.max(maxX, p.x);
    maxY = Math.max(maxY, p.y);
  };
  for (const e of entities) {
    if (e.type === "polyline") e.points.forEach(add);
    else if (e.type === "arc") for (let i = 0; i <= 16; i++) add(onCircle(e.center, e.r, e.startDeg + (e.sweepDeg * i) / 16));
  }
  return { minX, minY, maxX, maxY };
}

const round = (p: Vec): Vec => ({ x: Math.round(p.x * 1000) / 1000, y: Math.round(p.y * 1000) / 1000 });

function finish(made: Made, info: FittingInfo, name: string): LibraryItem {
  const box = bounds(made.b.entities);
  const c = { x: (box.minX + box.maxX) / 2, y: (box.minY + box.maxY) / 2 };
  const mv = (p: Vec): Vec => round({ x: p.x - c.x, y: p.y - c.y });
  const entities: Entity[] = made.b.entities.map((e) => {
    if (e.type === "polyline") return { ...e, points: e.points.map(mv) };
    if (e.type === "arc") return { ...e, center: mv(e.center) };
    return e;
  });
  const key = info.kind + "-" + info.size + (info.size2 && info.size2 !== info.size ? "x" + info.size2 : "");
  entities.push({
    id: `${key}-group`,
    type: "group",
    layerId: "fittings",
    locked: false,
    hidden: false,
    children: entities.map((e) => e.id),
    meta: {
      fitting: info,
      pivot: mv(made.inlet),
      ports: made.ports.map((pt) => ({ p: mv(pt.p), dir: { ...pt.dir }, size: pt.size, ...(pt.vertical ? { vertical: true } : {}) }))
    }
  });
  return {
    id: `builtin:${key}`,
    name,
    category: FITTING_CATEGORIES[info.kind],
    entities,
    size: { w: Math.round((box.maxX - box.minX) * 10) / 10, h: Math.round((box.maxY - box.minY) * 10) / 10 },
    units: "mm",
    updatedAt: "2026-10-06T00:00:00.000Z"
  };
}

let cache: LibraryItem[] | null = null;

/**
 * Every built-in fitting: 8 sizes × (90° bend, 45° bend, 90° bend up, 90° bend down, end cap) + 28 tees (7 equal, 21
 * reducing) each also branch up and branch down + 21 reducers = 40 + 84 + 21 = 145.
 */
export function builtInFittings(): LibraryItem[] {
  if (cache) return cache;
  const out: LibraryItem[] = [];
  for (const d of BEND_SIZES) {
    out.push(finish(bend(d, 90), { kind: "bend90", size: d }, `Bend 90° · ${d} mm`));
    out.push(finish(bend(d, 45), { kind: "bend45", size: d }, `Bend 45° · ${d} mm`));
    out.push(finish(bendVertical(d, true), { kind: "bendup", size: d }, bendVerticalName(d, true)));
    out.push(finish(bendVertical(d, false), { kind: "benddown", size: d }, bendVerticalName(d, false)));
    out.push(finish(cap(d), { kind: "cap", size: d }, `End cap · ${d} mm`));
  }
  for (const D of PIPE_SIZES) {
    for (const d of PIPE_SIZES) {
      if (d > D) continue;
      out.push(finish(tee(D, d), { kind: "tee", size: D, size2: d }, d === D ? `Tee · ${D} mm` : `Tee · ${D} mm, branch reduces to ${d} mm`));
      out.push(finish(teeVertical(D, d, true), { kind: "teeup", size: D, size2: d }, teeVerticalName(D, d, true)));
      out.push(finish(teeVertical(D, d, false), { kind: "teedown", size: D, size2: d }, teeVerticalName(D, d, false)));
      if (d < D) out.push(finish(reducer(D, d), { kind: "reducer", size: D, size2: d }, `Reducer · ${D} → ${d} mm`));
    }
  }
  cache = out;
  return out;
}

/**
 * One fitting of any size, as a library item (3.11.0). The built-in list holds the standard sizes; a pipe end
 * can be any size (a Davey pump inlet is DN65 or DN125), so the right-click menu on a pipe end makes the
 * fitting it needs here. Same geometry, names and ports as the built-in ones.
 */
export function makeFittingItem(kind: FittingKind, size: number, size2?: number): LibraryItem {
  switch (kind) {
    case "bend90":
      return finish(bend(size, 90), { kind, size }, `Bend 90° · ${size} mm`);
    case "bend45":
      return finish(bend(size, 45), { kind, size }, `Bend 45° · ${size} mm`);
    case "tee": {
      const d = size2 !== undefined && size2 < size ? size2 : size;
      return finish(tee(size, d), { kind, size, size2: d }, d === size ? `Tee · ${size} mm` : `Tee · ${size} mm, branch reduces to ${d} mm`);
    }
    case "reducer": {
      const d = size2 ?? size;
      if (!(d < size)) throw new Error("fittings: a reducer needs a smaller second size");
      return finish(reducer(size, d), { kind, size, size2: d }, `Reducer · ${size} → ${d} mm`);
    }
    case "teeup":
    case "teedown": {
      const up = kind === "teeup";
      const d = size2 !== undefined && size2 < size ? size2 : size;
      return finish(teeVertical(size, d, up), { kind, size, size2: d }, teeVerticalName(size, d, up));
    }
    case "bendup":
    case "benddown":
      return finish(bendVertical(size, kind === "bendup"), { kind, size }, bendVerticalName(size, kind === "bendup"));
    case "cap":
      return finish(cap(size), { kind, size }, `End cap · ${size} mm`);
  }
}

/** "Bend 90° · 100 mm · up (riser)" */
function bendVerticalName(d: number, up: boolean): string {
  return `Bend 90° · ${d} mm · ${up ? "up (riser)" : "down (drop)"}`;
}
